import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  // Public company logo for branding the login screen. Endpoint is read-only
  // and tolerates anonymous requests; we fall back to the "+" placeholder if
  // anything goes wrong (network, missing logo, etc.).
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  React.useEffect(() => {
    let cancelled = false;
    api.get("/company/branding").then(({ data }) => {
      if (!cancelled) setCompanyLogoUrl(data?.logo_url || null);
    }).catch(() => { /* OK — fall back to placeholder */ });
    return () => { cancelled = true; };
  }, []);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const logoSrc = companyLogoUrl ? `${BACKEND_URL}${companyLogoUrl}` : null;

  React.useEffect(() => {
    if (!user) return;
    // Once auth is established (either from a normal login or after the
    // invite-redeem effect below), send the user to their role's home page.
    // We bypass `/` -> HomeRedirect to avoid an extra render/flash.
    const dest = {
      super_admin: "/admin",
      dispatcher: "/dispatch",
      pharmacy_staff: "/staff",
      driver: "/driver",
    }[user.role] || "/";
    navigate(dest, { replace: true });
  }, [user, navigate]);

  // Auto-redeem ?invite=TOKEN from a driver QR magic link.
  // Defensive against:
  //   - React Strict Mode double-mount (a ref guard + immediate URL strip)
  //   - Stale invites being re-fired on refresh (URL is stripped BEFORE API call)
  //   - Race between setUser and navigate (navigation is handled by the [user] effect above)
  const redeemFiredRef = React.useRef(false);
  React.useEffect(() => {
    const invite = searchParams.get("invite");
    if (!invite || redeemFiredRef.current || user) return;
    redeemFiredRef.current = true;
    setInviteBusy(true);
    // Strip ?invite= immediately so neither Strict Mode's second pass nor a refresh
    // re-attempts to redeem an already-consumed token.
    searchParams.delete("invite");
    setSearchParams(searchParams, { replace: true });

    api.post("/auth/invite-login", { invite })
      .then(({ data }) => {
        localStorage.setItem("pr_token", data.token);
        // Setting the user fires the [user, navigate] effect above which routes
        // the driver straight to /driver.
        setUser(data.user);
      })
      .catch((err) => {
        setError(formatApiError(err) || "This invite link is invalid or has expired.");
        setInviteBusy(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1594935975218-a3596da034a3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(120,30%,15%)]/80 via-[hsl(120,30%,20%)]/60 to-[hsl(120,21%,40%)]/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/20 backdrop-blur grid place-items-center font-display font-bold text-xl border border-white/30 overflow-hidden">
              {logoSrc ? (
                <img src={logoSrc} alt="" className="h-full w-full object-contain" />
              ) : (
                <span aria-hidden="true">+</span>
              )}
            </div>
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">SRCI Pharm-Route</div>
              <div className="text-sm text-white/80">Delivery Planning Platform</div>
            </div>
          </div>
          <div className="space-y-3">
            <h1 className="text-4xl xl:text-5xl font-display font-bold leading-tight tracking-tight">
              Plan smarter routes.<br />Deliver care on time.
            </h1>
            <p className="text-white/85 text-base max-w-md leading-relaxed">
              Combine orders across pharmacies, auto-optimize multi-stop routes,
              and capture signed proof of delivery — all in one place.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-4 max-w-md">
              {["Multi-pharmacy", "Auto-optimize", "Live POD"].map((t) => (
                <div key={t} className="px-3 py-2 rounded-lg bg-white/10 backdrop-blur border border-white/15 text-xs text-center">{t}</div>
              ))}
            </div>
            {/* Legal links — under the feature chips, light enough not to compete
                with the headline but readable for compliance access. */}
            <div className="flex flex-wrap items-center gap-3 pt-3 max-w-md text-[11px]" data-testid="login-legal-links">
              <a
                href="/sms-consent"
                className="text-white/70 hover:text-white underline-offset-2 hover:underline transition-colors"
                data-testid="login-link-sms-consent"
              >
                SMS Consent
              </a>
              <span className="text-white/30">·</span>
              <a
                href="/privacy-policy"
                className="text-white/70 hover:text-white underline-offset-2 hover:underline transition-colors"
                data-testid="login-link-privacy"
              >
                Privacy Policy
              </a>
              <span className="text-white/30">·</span>
              <a
                href="/terms-conditions"
                className="text-white/70 hover:text-white underline-offset-2 hover:underline transition-colors"
                data-testid="login-link-terms"
              >
                Terms &amp; Conditions
              </a>
            </div>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} SRCI Pharm-Route • Built for pharmacy logistics</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {inviteBusy && (
            <div className="mb-4 p-3 rounded-lg bg-primary/10 text-primary text-sm font-medium" data-testid="invite-redeeming">
              Signing you in from your invite link…
            </div>
          )}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground grid place-items-center font-display font-bold text-lg overflow-hidden">
              {logoSrc ? (
                <img src={logoSrc} alt="" className="h-full w-full object-contain" />
              ) : (
                <span aria-hidden="true">+</span>
              )}
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">SRCI Pharm-Route</div>
              <div className="text-xs text-muted-foreground mt-1">Delivery Planner</div>
            </div>
          </div>

          <h2 className="text-3xl font-display font-bold tracking-tight">Welcome back</h2>
          <p className="text-muted-foreground mt-1.5 text-sm">Sign in to manage your delivery operations.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@pharmacy.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-full text-base"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>

          <Card className="mt-8 p-5 bg-secondary/50 border-border" data-testid="demo-accounts-link">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Evaluating the platform?</div>
                <p className="text-xs text-muted-foreground mt-0.5">Sign in as any role with a single click.</p>
              </div>
              <Link
                to="/demo-access"
                className="text-sm font-medium text-primary hover:underline whitespace-nowrap"
                data-testid="demo-accounts-cta"
              >
                Demo access →
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
